import type { CoachAvailability } from '@court-sync/shared';

export interface TimeRange {
  startTime: string;
  endTime: string;
}

function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Groups available CoachAvailability slots by dayOfWeek and merges consecutive
 * 30-min slots into contiguous time ranges.
 * Returns a Map<dayOfWeek, TimeRange[]> containing only days that have available slots.
 */
export function groupAvailabilityByDay(slots: CoachAvailability[]): Map<number, TimeRange[]> {
  const availableSlots = slots.filter((s) => s.isAvailable);

  const byDay = new Map<number, { start: number; end: number }[]>();
  for (const slot of availableSlots) {
    const start = timeToMinutes(slot.startTime);
    const end = timeToMinutes(slot.endTime);
    if (!byDay.has(slot.dayOfWeek)) byDay.set(slot.dayOfWeek, []);
    const daySlots = byDay.get(slot.dayOfWeek);
    if (daySlots) daySlots.push({ start, end });
  }

  const result = new Map<number, TimeRange[]>();
  for (const [day, intervals] of byDay.entries()) {
    const sorted = intervals.sort((a, b) => a.start - b.start);
    const merged: { start: number; end: number }[] = [];

    for (const interval of sorted) {
      if (merged.length === 0 || interval.start > merged[merged.length - 1].end) {
        merged.push({ ...interval });
      } else {
        merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, interval.end);
      }
    }

    result.set(
      day,
      merged.map(({ start, end }) => ({
        startTime: minutesToTime(start),
        endTime: minutesToTime(end),
      }))
    );
  }

  return result;
}

/**
 * Formats a HH:MM time range into a human-readable "9:00am – 5:00pm" string.
 */
export function formatTimeRange(startTime: string, endTime: string): string {
  return `${formatTime(startTime)} – ${formatTime(endTime)}`;
}

function formatTime(time: string): string {
  const [hourStr, minuteStr] = time.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  const period = hour >= 12 ? 'pm' : 'am';
  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;
  return `${hour}:${String(minute).padStart(2, '0')}${period}`;
}
